import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import styled from 'astroturf/react'
import { Button } from '@calendly/ui/components/button'
import { TagContainer, TagInput } from '@calendly/tags'
import { FieldError, FieldLabel } from '@calendly/form/parts'
import { HtmlParser } from '@calendly/html-parser'
const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
const textColorSecondary1 = 'var(--text-color-secondary-1, rgba(51, 51, 51, 1))'
import { IconUserPlus } from '@calendly/ui/components/glyphs/IconUserPlus'
import { useFlag } from '@calendly/feature-check-react'
import { MinimalButton } from '@calendly/ui/components/minimal-button'

import { useI18n } from 'locales'
import { useBooleanState } from 'hooks/useBooleanState'
import BookingField from 'components/enter_details/booking_field'
import {
  isEnterKey,
  isSpaceKey,
  isTabKey,
  isCommaKey,
  isBackspaceKey
} from 'services/keycodes'
import { DELIMITER_CHARS, DELIMITER_CHARS_REGEXP, VALID_EMAIL_REGEXP } from 'components/enter_details/fields/guest/constants'

import Tag from './tag'
import _StyledTagContainer from "index-StyledTagContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/fields/guest/index.js?styleId=StyledTagContainer";
import _AddButtonContainer from "index-AddButtonContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/fields/guest/index.js?styleId=AddButtonContainer";
import _LimitNotice from "index-LimitNotice.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/fields/guest/index.js?styleId=LimitNotice";
import _Container from "index-Container.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/fields/guest/index.js?styleId=Container";


const StyledTagContainer = /*#__PURE__*/styled(TagContainer, null, {
  displayName: "StyledTagContainer",
  styles: _StyledTagContainer
})

const AddButtonContainer = /*#__PURE__*/styled('div', null, {
  displayName: "AddButtonContainer",
  styles: _AddButtonContainer
})

const LimitNotice = /*#__PURE__*/styled('small', null, {
  displayName: "LimitNotice",
  styles: _LimitNotice
})

const Container = /*#__PURE__*/styled('div', null, {
  displayName: "Container",
  styles: _Container
})

const GuestsField = ({
  guests,
  invalidEmails,
  errors,
  onAdd,
  onUpdate,
  onRemove,
  dismissError
}) => {
  const { i18n } = useI18n()
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const [isActive, activate] = useBooleanState(guests.length > 0)
  const [isFocused, focus, blur] = useBooleanState(false)
  const [inputValue, setInputValue] = useState('')
  const tagInputRef = useRef(null)

  const invalidEmailsSet = useMemo(() => new Set(invalidEmails), [invalidEmails])

  const tags = useMemo(() => {
    const isValidEmail = (email) => {
      if (invalidEmailsSet.has(email)) {
        return false
      }

      return VALID_EMAIL_REGEXP.test(email)
    }

    return guests.map(({ id, email }) => ({
      id,
      email,
      isValid: isValidEmail(email),
      onChange: (newEmail) => onUpdate(id, newEmail)
    }))
  }, [guests, onUpdate, invalidEmailsSet])

  const guestsRef = useCallback((node) => {
    if (node) {
      node.scrollIntoView()
    }
  }, [])

  const handleFocus = useCallback(() => {
    focus()
  }, [focus])

  const handleAddGuestsClick = useCallback(() => {
    activate()
    focus()
  }, [activate, focus])

  const handleAdd = useCallback(() => {
    if (inputValue.length) {
      onAdd(inputValue)
    }
    setInputValue('')
  }, [onAdd, inputValue])

  const handleAddOnBlur = useCallback(() => {
    if (inputValue.length) {
      onAdd(inputValue)
    }

    setInputValue('')
    blur()
  }, [onAdd, blur, inputValue])

  const handleInputChange = useCallback((value) => {
    setInputValue(value)
    dismissError()
  }, [dismissError])

  const handleBeforeInput = useCallback((event) => {
    if (DELIMITER_CHARS.includes(event.data)) {
      handleAdd()
      event.preventDefault()
    }
  }, [handleAdd])

  const handleKeyDown = useCallback((event) => {
    if (isEnterKey(event) || isCommaKey(event)
    || isSpaceKey(event)) {
      handleAdd()
      event.preventDefault()
    } else if (isBackspaceKey(event)) {
      if (!inputValue.length) {
        dismissError()
        const lastTagId = tags[tags.length - 1].id
        onRemove(lastTagId)
      }
    } else if (isTabKey(event)) {
      if (inputValue.length) {
        handleAdd()
        event.preventDefault()
      }
    }
  }, [handleAdd, inputValue.length, dismissError, tags, onRemove])

  const handlePaste = useCallback((event) => {
    const pasteText = event.clipboardData.getData('Text')
    pasteText
      .split(DELIMITER_CHARS_REGEXP)
      .filter(Boolean)
      .map((email) => email.trim())
      .forEach(onAdd)

    event.preventDefault()
  }, [onAdd])

  const handleMouseDown = useCallback(() => {
    focus()
  }, [focus])

  useEffect(() => {
    if (guests.length > 0) activate()
  }, [guests.length, activate])

  if (!isActive) {
    return (
      <AddButtonContainer>
        {bookingAppRedesign ? (
          <MinimalButton
            appearance="primary"
            size="small"
            leadingIcon={IconUserPlus}
            onClick={handleAddGuestsClick}
          >
            {i18n.t('booking.templates.event_guests.add_guests')}
          </MinimalButton>
        ) : (
          <Button
            decoration="outline"
            size="small"
            onClick={handleAddGuestsClick}
          >
            {i18n.t('booking.templates.event_guests.add_guests')}
          </Button>
        )}
      </AddButtonContainer>
    )
  }

  const error = Array.isArray(errors) ? HtmlParser(errors.join('<br />')) : errors
  const isError = Boolean(error.length)

  return (
    <BookingField>
      <div ref={guestsRef}>
        <Container>
          <FieldLabel
            text={i18n.t('booking.templates.event_guests.guest_emails')}
            htmlFor="invitee_guest_input"
          />
          <StyledTagContainer
            isError={isError}
            ref={tagInputRef}
            onKeyDown={handleKeyDown}
            onInputChange={handleInputChange}
            onBeforeInput={handleBeforeInput}
            onPaste={handlePaste}
            onInputFocus={handleFocus}
            onInputBlur={handleAddOnBlur}
            isFocused={isFocused}
            onMouseDown={handleMouseDown}
            inputValue={inputValue}
            inputAttributes={{
              type: 'email',
              id: 'invitee_guest_input',
              'aria-invalid': isError,
              'aria-describedby': isError ? 'invitee_guest_errors' : undefined,
              'aria-expanded': isActive,
              'aria-controls': 'invitee_guest_input',
              'aria-label': i18n.t('booking.templates.event_guests.guest_emails')
            }}
            data-testid="event_guest_emails"
          >
            {tags.map(({ id, email, onChange, isValid }) => (
              <Tag
                key={id}
                id={id}
                name={email}
                isValid={isValid}
                onChange={onChange}
                onDelete={onRemove}
                tagInputRef={tagInputRef}
              />
            ))}
            <TagInput />
          </StyledTagContainer>
          <FieldError text={isError && error} id="invitee_guest_errors" />
        </Container>
        {!isError && (
          <LimitNotice>
            {i18n.t('booking.templates.event_guests.limit_notice')}
          </LimitNotice>
        )}
      </div>
    </BookingField>
  )
}

export default GuestsField
